import { useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../shared/ui/button/Button";
import Checkbox from "../../../../shared/ui/form/input/Checkbox";
import Label from "../../../../shared/ui/form/Label";
import Input from "../../../../shared/ui/form/input/InputField";
import { Modal } from "../../../../shared/ui/modal";
import { useInviteMember, CenterRole, CenterType } from "../../../center";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    centerId: string;
    type: CenterType;
}

const ERRORS: Record<string, string> = {
    already_a_member: "center.invitations.errorAlreadyMember",
    member_of_another_center: "center.invitations.errorOtherCenter",
    invitation_already_pending: "center.invitations.errorAlreadyInvited",
    no_seats_left: "center.invitations.seatsFull",
};

/**
 * Inviting somebody into the centre.
 *
 * The invitation binds to the address typed here, grants the roles ticked,
 * and holds a seat until it is used or revoked. The join link itself appears
 * in the list behind this dialog, whether or not the email arrives.
 */
export default function InviteMemberModal({ isOpen, onClose, centerId, type }: Props) {
    const { t, i18n } = useTranslation();
    const invite = useInviteMember(centerId);

    const [email, setEmail] = useState("");
    const [asAdmin, setAsAdmin] = useState(false);
    const [asTherapist, setAsTherapist] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const roles: CenterRole[] = [
        ...(asAdmin ? (["admin"] as CenterRole[]) : []),
        ...(asTherapist ? (["therapist"] as CenterRole[]) : []),
    ];
    const canInvite = email.trim().length > 0 && roles.length > 0 && !invite.isPending;

    const close = () => {
        setEmail("");
        setAsAdmin(false);
        setAsTherapist(true);
        setError(null);
        onClose();
    };

    const handleInvite = async () => {
        setError(null);
        try {
            // The email goes out in the administrator's language: the person
            // invited may have no account, and so no language of their own, yet.
            await invite.mutateAsync({
                email: email.trim(),
                roles,
                language: i18n?.language?.startsWith("en") ? "en" : "es",
            });
            close();
        } catch (err: unknown) {
            const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail;
            setError(t((detail && ERRORS[detail]) || "center.invitations.errorGeneric"));
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={close}
            className="mx-4 w-full max-w-md rounded-2xl bg-surface-raised p-6"
        >
            <form
                role="dialog"
                aria-label={t("center.invitations.inviteTitle")}
                className="flex flex-col gap-4"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleInvite();
                }}
            >
                <div>
                    <h3 className="text-lg font-semibold text-content-primary">
                        {t("center.invitations.inviteTitle")}
                    </h3>
                    <p className="mt-1 text-sm text-content-tertiary">
                        {type === "training"
                            ? t("center.invitations.subtitleTraining")
                            : t("center.invitations.subtitleTherapy")}
                    </p>
                </div>

                <div>
                    <Label htmlFor="invite-email">{t("center.invitations.email")}</Label>
                    <Input
                        id="invite-email"
                        name="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t("center.invitations.emailPlaceholder")}
                    />
                </div>

                <fieldset className="flex flex-col gap-2">
                    <legend className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-400">
                        {t("center.invitations.roles")}
                    </legend>
                    <Checkbox
                        id="invite-role-therapist"
                        checked={asTherapist}
                        onChange={setAsTherapist}
                        label={
                            type === "training"
                                ? t("center.invitations.roleTrainer")
                                : t("center.invitations.roleTherapist")
                        }
                    />
                    <Checkbox
                        id="invite-role-admin"
                        checked={asAdmin}
                        onChange={setAsAdmin}
                        label={t("center.invitations.roleAdmin")}
                    />
                    {roles.length === 0 && (
                        <p className="text-sm text-red-600 dark:text-red-400">
                            {t("center.invitations.rolesRequired")}
                        </p>
                    )}
                </fieldset>

                {error && (
                    <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                        {error}
                    </p>
                )}

                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={close}>
                        {t("common.cancel")}
                    </Button>
                    <Button type="submit" disabled={!canInvite} loading={invite.isPending}>
                        {t("center.invitations.submit")}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
